import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Check, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Offers = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchOffers();
  }, [statusFilter]);

  const fetchOffers = async () => {
    try {
      const url = statusFilter 
        ? `${BACKEND_URL}/api/admin/offers?status=${statusFilter}`
        : `${BACKEND_URL}/api/admin/offers`;
      
      const response = await fetch(url, { credentials: 'include' });
      
      if (response.ok) {
        const data = await response.json();
        setOffers(data);
      }
    } catch (error) {
      console.error('Failed to fetch offers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (offerId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/offers/${offerId}/accept`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (response.ok) {
        fetchOffers();
      }
    } catch (error) {
      console.error('Failed to accept offer:', error);
    }
  };

  const handleReject = async (offerId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/offers/${offerId}/reject`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (response.ok) {
        fetchOffers();
      }
    } catch (error) {
      console.error('Failed to reject offer:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Offers</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage product offers</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Product</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">User</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Offer Amount</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Product Price</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Status</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Date</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-gray-500">Loading...</td>
                  </tr>
                ) : offers.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-gray-500">No offers found</td>
                  </tr>
                ) : (
                  offers.map((offer) => (
                    <tr key={offer.id} className="border-t dark:border-gray-700">
                      <td className="py-3 px-4">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {offer.product?.name || 'Unknown Product'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-gray-900 dark:text-white">{offer.user?.name}</div>
                        <div className="text-sm text-gray-500">{offer.user?.email}</div>
                      </td>
                      <td className="py-3 px-4 text-green-600 font-medium">₹{offer.offer_amount}</td>
                      <td className="py-3 px-4 text-gray-900 dark:text-white">₹{offer.product?.price}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded text-xs ${getStatusColor(offer.status)}`}>
                          {offer.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400">
                        {new Date(offer.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4">
                        {offer.status === 'pending' && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleAccept(offer.id)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded"
                              title="Accept"
                            >
                              <Check size={16} />
                            </button>
                            <button
                              onClick={() => handleReject(offer.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                              title="Reject"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Offers;